import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createChatNavigator } from '../utils/chatNavigation';
import { TTYD_3 } from '../constants/Constantsi18';
import { useNewChatTrigger } from './useNewChatTrigger';
import { executeWithDelaysAsync } from '../utils/delay';

export const useChatHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { triggerNewChat, triggerChatReload } = useNewChatTrigger();
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  
  const { userInfo } = useSelector((state: any) => ({
    userInfo: state.loginResource,
  }));
  
  // Get current conversation ID from URL params
  const currentConversationId = searchParams.get('conversationId');

  // Close ChatHistory when clicking outside
  //todo improve this
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showChatHistory && 
          chatHistoryRef.current && 
          !chatHistoryRef.current.contains(event.target as Node)) {
        
        const target = event.target as Element;
        
        // Check if the click is on a ChatHistory-related element (popovers, dropdowns, etc.)
        const isOnChatHistoryElement = (
          // Check for common popover/dropdown classes
          target.closest('[role="dialog"]') ||
          target.closest('[role="menu"]') ||
          target.closest('[role="tooltip"]') ||
          target.closest('[role="modal-dialog-box"]') ||
          target.closest('[data-popper-root]') ||
          target.closest('.MuiPopover-root') ||
          target.closest('.MuiMenu-root') ||
          target.closest('.ant-dropdown') ||
          target.closest('.ant-popover') ||
          // Check for Axtria Design System modal components
          target.closest('.AdsModalDialogBox-root') ||
          target.closest('[class*="AdsModalDialogBox"]') ||
          target.closest('[class*="AdsButton"]') ||
          target.closest('[class*="AdsTypography"]') ||
          // Check for any element with ChatHistory in class name
          target.closest('[class*="ChatHistory"]') ||
          target.closest('[class*="chat-history"]') ||
          // Check if it's a portal element
          target.closest('[data-portal]') ||
          // Check for common modal/overlay containers
          target.closest('#root > div:last-child') // Common portal location
        );

        if (!isOnChatHistoryElement) {
          setShowChatHistory(false);
        } else {

        }
      }
    };

    // Add event listener when ChatHistory is shown
    if (showChatHistory) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showChatHistory]);

  // Helper function to get team name
  const getTeamName = () => {
    try {
      if (!userInfo?.company_data || !userInfo?.preferredCompany || !userInfo?.preferredProject) {
        return TTYD_3;
      }

      // Find the preferred company
      const company = userInfo.company_data.find((company: any) => 
        company.companyId === userInfo.preferredCompany
      );
      
      if (!company) {
        return TTYD_3;
      }

      // Get the project identifier (handle both array and single value cases)
      const projectIdentifier = Array.isArray(userInfo.preferredProject) 
        ? userInfo.preferredProject[0]?.project_name || userInfo.preferredProject[0]
        : userInfo.preferredProject;

      

      // Find the project in the company's project list
      const project = company.projectList?.find((project: any) => 
        project.project_id === projectIdentifier || 
        project.project_name === projectIdentifier
      );

      if (!project) {

        return TTYD_3;
      }

      // Return the team name (assuming it's project.project_name or project.team_name)
      const teamName = project.team_name || project.project_name || project.project_id;

      return teamName;
      
    } catch (error) {

      return TTYD_3;
    }
  };

  // Handle new chat navigation
  const handleNewChat = () => {
    
    // Close chat history first to prevent state conflicts
    setShowChatHistory(false);
    
    // Check if we're already on the chat page
    if (location.pathname === '/chat') {
      // If already on chat page, trigger global rerender to reset the component
      triggerNewChat();
      // Reset navigation flag immediately since we're not actually navigating
      setIsNavigating(false);
    } else {
      // Set flag to prevent other navigation
      setIsNavigating(true);
      
      // Use delay utility to ensure the state change happens first
      executeWithDelaysAsync([
        {
          callback: () => navigate(`/chat`, { replace: false }),
          delay: 50
        },
        {
          callback: () => setIsNavigating(false),
          delay: 100
        }
      ]);
    }
  };

  // Handle chat selection navigation
  //todo need to review again
  const handleChatSelect = (chat: any) => {

    // Set flag to prevent other navigation
    setIsNavigating(true);
    // Close chat history first to prevent state conflicts
    setShowChatHistory(false);
    
    // Use delay utility to ensure the state change happens first
    //todo need approach for the same
    executeWithDelaysAsync([
      {
        callback: () => createChatNavigator(chat, navigate),
        delay: 50
      },
      {
        callback: () => setIsNavigating(false),
        delay: 100
      }
    ]);
  };

  // Handle navigation hover
  const handleNavMouseOver = (nav: any) => {
    const currentPortfolio = userInfo?.userPortfolios?.find(
      (pf: any) => pf.project_id == userInfo?.preferredProject
    );
    const isPortfolioIdMissing = !currentPortfolio?.is_complete;
    const currentCompany = userInfo?.company_data?.find((c: any) => c.companyId == userInfo?.preferredCompany);
    const currentProject = currentCompany?.projectList?.find((p: any) => p.project_id == userInfo?.preferredProject);
    const isOnboardingEnabled = currentProject?.onboarding && currentProject?.onboarding !== false;
    const isInOnboardingFlow = location.pathname === '/' && !userInfo?.isHomepageDashboard;
    const isLocked = isOnboardingEnabled && (isPortfolioIdMissing || isInOnboardingFlow);

    nav.id === "History" && !isLocked ? setShowChatHistory(true) : setShowChatHistory(false);
  };

  // Handle conversation deletion
  //todo need to review again
  const handleDelete = (conversationId: string) => {
    
    // Check if the deleted conversation is currently active
    if (currentConversationId === conversationId) {
      // If we're deleting the current conversation, navigate to new chat
    
      // Close chat history first to prevent UI conflicts
      setShowChatHistory(false);
      
      // Set navigation flag to prevent other navigation conflicts
      setIsNavigating(true);
      
      // Use delay utility to ensure state updates happen first, then navigate
      executeWithDelaysAsync([
        {
          callback: () => navigate('/chat', { replace: true }),
          delay: 50
        },
        {
          callback: () => setIsNavigating(false),
          delay: 100
        }
      ]);
    }
    
    // Here you would typically make an API call to delete the conversation
    // Example: await deleteConversationAPI(conversationId);
  };

  // Handle conversation rename
  const handleRename = (conversationId: string, newTitle?: string) => {
    
    // Check if the renamed conversation is currently active
    if (currentConversationId === conversationId) {
      // If we're renaming the current conversation, reload/refresh the chat without navigation

      // Trigger a rerender of the chat component to reflect the new title (without navigation)
      triggerChatReload();
    }
    
    // Here you would typically make an API call to rename the conversation
    // Example: await renameConversationAPI(conversationId, newTitle);
  };

  return {
    showChatHistory,
    setShowChatHistory,
    isNavigating,
    setIsNavigating,
    chatHistoryRef,
    userInfo,
    getTeamName,
    handleNewChat,
    handleChatSelect,
    handleNavMouseOver,
    handleDelete,
    handleRename,
  };
};
